import { api } from '@/api';
import { useEffect, useRef, useState } from 'react';

export interface ConsumptionBookingSummary {
  id: string;
  code?: string;
  journeyOrigin?: { time?: string; name?: string } | null;
  journeyDestination?: { time?: string; name?: string } | null;
}

// Resolves travel-account consumption booking references into displayable
// booking summaries via the generic bookings search (BR-54939).
//
// `consumption.bookingId` carries the booking NUMBER, not a translator
// booking id — fetching GET /bookings/{number} directly is a forbidden
// route (403). The generic search accepts the number as `reference` and
// returns everything the usage card needs: the translator booking `id`
// (for View details), the booking `code`, and the journey origin /
// destination names with the first-trip time.
//
// Lookups are keyed per booking reference; a failed or empty lookup stores
// null so the card falls back to the data embedded in the consumption
// itself and the search is not retried in a loop.
export const useConsumptionBookingLookup = (
  bookingRefs: Array<string>,
  email: string | null | undefined,
  enabled: boolean
): Record<string, ConsumptionBookingSummary | null> => {
  const [summaries, setSummaries] = useState<
    Record<string, ConsumptionBookingSummary | null>
  >({});
  const inFlightRef = useRef<Set<string>>(new Set());

  // Stable key so the effect re-runs only when the actual set changes.
  const refsKey = Array.from(new Set(bookingRefs)).sort().join('|');

  useEffect(() => {
    if (!enabled || !email || !refsKey) return;

    let cancelled = false;

    const pending = refsKey
      .split('|')
      .filter(
        (ref) => ref && !(ref in summaries) && !inFlightRef.current.has(ref)
      );
    if (pending.length === 0) return;

    pending.forEach((ref) => inFlightRef.current.add(ref));

    void Promise.all(
      pending.map(async (ref) => {
        try {
          const { data } = await api.proxyApi.post<{
            bookingsSearchResults?: Array<ConsumptionBookingSummary>;
          }>('/bookings-search/generic', {
            reference: ref,
            purchaserPassengerEmail: email,
          });
          const result = data.bookingsSearchResults?.[0] ?? null;
          return [ref, result] as const;
        } catch {
          return [ref, null] as const;
        }
      })
    ).then((entries) => {
      entries.forEach(([ref]) => inFlightRef.current.delete(ref));
      if (cancelled) return;
      setSummaries((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    });

    return () => {
      cancelled = true;
    };
    // `summaries` is deliberately read but not depended on: adding it would
    // re-run the effect on every resolution for nothing — `pending` already
    // filters out resolved refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, email, refsKey]);

  return summaries;
};
